import Debug from 'debug';
import { LoadStats } from 'hls.js/src/loader/load-stats';
import XhrLoader from 'hls.js/src/utils/xhr-loader';
import { Events } from './interface';
function createSegment(context, data = undefined) {
    var _a;
    const streamId = context === null || context === void 0 ? void 0 : context.url.replace(/\?.*/g, '').split('/').pop();
    const segment = {
        id: `${(_a = context === null || context === void 0 ? void 0 : context.frag) === null || _a === void 0 ? void 0 : _a.baseurl}+${streamId}`,
        url: context === null || context === void 0 ? void 0 : context.url,
        streamId,
        range: undefined,
        data: data === null || data === void 0 ? void 0 : data.slice(0),
    };
    return segment;
}
class P2PFragmentLoader {
    constructor(engine) {
        this.debug = Debug('p2pml:p2p-fragment-loader');
        this.callbacks = null;
        this.config = null;
        this.stats = new LoadStats();
        this.engine = engine;
    }
    destroy() {
        this.context = null;
        this.stats = new LoadStats();
    }
    abort() {
        var _a;
        this.abortInternal();
        if ((_a = this.callbacks) === null || _a === void 0 ? void 0 : _a.onAbort) {
            this.callbacks.onAbort(this.stats, this.context, null);
        }
    }
    abortInternal() {
        self.clearTimeout(this.requestTimeout);
        this.stats.aborted = true;
        const p2pManager = this.engine.p2pManager;
        const segment = createSegment(this.context);
        p2pManager.abort(segment);
    }
    load(context, config, callbacks) {
        if (this.stats.loading.start) {
            throw new Error('Loader can only be used once.');
        }
        this.stats.loading.start = self.performance.now();
        this.context = context;
        this.config = config;
        this.callbacks = callbacks;
        return this.loadInternal();
    }
    loadInternal() {
        var _a, _b;
        const { config, context } = this;
        if (!config || !context) {
            return;
        }
        const stats = this.stats;
        stats.loading.first = 0;
        stats.loaded = 0;
        stats.aborted = false;
        if (context.rangeStart) {
            this.debug('p2p 暂时不支持 range 请求', context.rangeStart, context.rangeEnd);
            (_a = this.callbacks) === null || _a === void 0 ? void 0 : _a.onError({ code: 0, text: 'p2p 暂时不支持 range 请求' }, context, null);
            return;
        }
        const fragDuration = ((_b = context === null || context === void 0 ? void 0 : context.frag) === null || _b === void 0 ? void 0 : _b.duration) * 1000 || 3 * 1000;
        const p2pSegmentDownloadTimeout = this.engine.getSettings().p2pSegmentDownloadTimeout || 3 * 1000;
        const minTimeout = Math.min(fragDuration, p2pSegmentDownloadTimeout) / 2;
        self.clearTimeout(this.requestTimeout);
        this.requestTimeout = self.setTimeout(() => {
            var _a;
            this.abortInternal();
            if ((_a = this.callbacks) === null || _a === void 0 ? void 0 : _a.onTimeout) {
                this.callbacks.onTimeout(stats, context, null);
            }
        }, minTimeout);
        const p2pManager = this.engine.p2pManager;
        const segment = createSegment(this.context);
        const p2pCallbacks = {
            onSuccess: (peerId, data) => {
                var _a, _b;
                self.clearTimeout(this.requestTimeout);
                this.stats.loading.end = Math.max(self.performance.now(), this.stats.loading.first);
                this.stats.total = this.stats.loaded = data.byteLength;
                const onProgress = (_a = this.callbacks) === null || _a === void 0 ? void 0 : _a.onProgress;
                if (onProgress) {
                    onProgress(this.stats, this.context, data, null);
                }
                this.debug('p2p 下载分片成功', segment.id, this.stats);
                const response = {
                    url: segment.url || '',
                    data: data,
                    code: 200,
                };
                (_b = this.callbacks) === null || _b === void 0 ? void 0 : _b.onSuccess(response, this.stats, this.context, null);
            },
            onError: (peerId, error) => {
                var _a;
                self.clearTimeout(this.requestTimeout);
                this.debug('p2p 下载分片失败', segment.id, peerId, error);
                (_a = this.callbacks) === null || _a === void 0 ? void 0 : _a.onError(error, this.context, null);
            },
            onPiece: (peerId, pieceData) => {
                if (this.stats.loading.first === 0) {
                    this.stats.loading.first = Math.max(self.performance.now(), this.stats.loading.start);
                }
            },
        };
        p2pManager.load(segment, p2pCallbacks);
    }
    getCacheAge() {
        return null;
    }
}
export class HybridFragmentLoader {
    constructor(hlsConfig, engine) {
        this.debug = Debug('p2pml:p2p-hybrid-loader');
        this.hlsConfig = null;
        this.xhrLoader = null;
        this.callbacks = null;
        this.hlsConfig = hlsConfig;
        this.engine = engine;
        this.stats = new LoadStats();
    }
    async load(context, config, callbacks) {
        let keepP2PTimeout = 0;
        if (HybridFragmentLoader.CacheStatus.latestUseP2P) {
            this.debug('上次使用了 p2p，等待 100ms');
            keepP2PTimeout = 100;
        }
        setTimeout(() => {
            this.pendingLoad(context, config, callbacks);
        }, keepP2PTimeout);
    }
    pendingLoad(context, config, callbacks) {
        var _a;
        const segment = createSegment(context);
        const pendingTimeout = this.engine.p2pManager.checkPendingTimeout(segment);
        const timeout = Math.min((((_a = context === null || context === void 0 ? void 0 : context.frag) === null || _a === void 0 ? void 0 : _a.duration) * 1000) / 2, pendingTimeout || 0);
        this.debug('等待 pending 下载的超时时间', timeout);
        setTimeout(() => {
            const canP2P = this.engine.p2pManager.canDownload(segment);
            if (canP2P) {
                HybridFragmentLoader.CacheStatus.latestUseP2P = true;
                this.p2pLoad(context, config, callbacks);
            }
            else {
                HybridFragmentLoader.CacheStatus.latestUseP2P = false;
                this.xhrLoad(context, config, callbacks);
            }
        }, timeout);
    }
    xhrLoad(context, config, callbacks) {
        this.xhrLoader = new XhrLoader(this.hlsConfig);
        this.callbacks = {
            onSuccess: callbacks.onSuccess,
            onError: callbacks.onError,
            onTimeout: callbacks.onTimeout,
            onAbort: callbacks.onAbort,
            onProgress: (stats, context, data, networkDetails) => {
                this.stats = stats;
                const segment = createSegment(context, data);
                this.engine.storeSegmentAndDelay(segment, { http: stats.loading.end - stats.loading.start });
                this.engine.emit(Events.SegmentLoaded, segment, null);
                callbacks.onProgress(stats, context, data, networkDetails);
            },
        };
        const segment = createSegment(context);
        this.engine.p2pManager.sendSegmentsPendingToAll(segment.id, this.engine.lastPendingDelay.http);
        this.debug('使用 xhr 下载', segment.id);
        this.xhrLoader.load(context, config, this.callbacks);
    }
    p2pLoad(context, config, callbacks) {
        this.p2pLoader = new P2PFragmentLoader(this.engine);
        this.callbacks = {
            onSuccess: callbacks.onSuccess,
            onError: () => {
                this.xhrLoad(context, config, callbacks);
            },
            onTimeout: () => {
                this.xhrLoad(context, config, callbacks);
            },
            onAbort: callbacks.onAbort,
            onProgress: (stats, context, data, networkDetails) => {
                this.stats = stats;
                const segment = createSegment(context, data);
                this.engine.storeSegmentAndDelay(segment, { p2p: stats.loading.end - stats.loading.start });
                callbacks === null || callbacks === void 0 ? void 0 : callbacks.onProgress(stats, context, data, networkDetails);
            },
        };
        const segment = createSegment(context);
        this.engine.p2pManager.sendSegmentsPendingToAll(segment.id, this.engine.lastPendingDelay.p2p);
        this.p2pLoader.load(context, config, this.callbacks);
    }
    abort() {
        var _a, _b;
        (_a = this.xhrLoader) === null || _a === void 0 ? void 0 : _a.abort();
        (_b = this.p2pLoader) === null || _b === void 0 ? void 0 : _b.abort();
    }
    destroy() {
        var _a, _b;
        this.callbacks = null;
        (_a = this.xhrLoader) === null || _a === void 0 ? void 0 : _a.destroy();
        (_b = this.p2pLoader) === null || _b === void 0 ? void 0 : _b.destroy();
    }
    getCacheAge() {
        var _a;
        return (_a = this.xhrLoader) === null || _a === void 0 ? void 0 : _a.getCacheAge();
    }
}
HybridFragmentLoader.CacheStatus = { latestUseP2P: false };
